/**
 * Exercise Selection Modal - Single User Assignment
 * 
 * This modal allows selecting multiple exercises to assign to a single user.
 * Unlike the ExerciseAttachmentModal which handles multiple users, this is
 * simplified for single-user exercise assignment.
 */

import React, { useState, useEffect } from "react";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import { TExercise } from "../../../utils/constants";
import { IUserReport } from "../../../services/types/users.types";
import { useAppSelector } from "../../../services/hooks";
import { adminSelector } from "../../../services/selectors/user.selectors";
import { exercisesCategoriesSelector } from "../../../services/selectors/utils.selectors";
import { filterObjectsByString } from "../../../utils/search";
import { getCategory } from "../../../utils/utils";
import { useBulkAssignExercisesMutation } from "../../../services/api/admin";
import styles from "./exerciseSelectionModal.module.css";

interface ExerciseSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  exercises: TExercise[];
  user: IUserReport;
  onSuccess?: () => void;
}

const ExerciseSelectionModal: React.FC<ExerciseSelectionModalProps> = ({
  isOpen,
  onClose,
  exercises,
  user,
  onSuccess,
}) => {
  const admin = useAppSelector(adminSelector);
  const exercisesCategories = useAppSelector(exercisesCategoriesSelector);
  const [bulkAssignExercises, { isLoading: isAssigning }] = useBulkAssignExercisesMutation();
  
  const [selectedExercises, setSelectedExercises] = useState<TExercise[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Reset modal state when closing
  useEffect(() => {
    if (!isOpen) {
      setSelectedExercises([]);
      setSearchValue("");
      setShowInactive(false);
      setShowError(false);
      setErrorMessage("");
    }
  }, [isOpen]);

  const handleExerciseToggle = (exercise: TExercise) => {
    setSelectedExercises(prev => {
      const exists = prev.find(e => e.id === exercise.id);
      if (exists) {
        return prev.filter(e => e.id !== exercise.id);
      }
      return [...prev, exercise];
    });
  };

  const handleSelectAll = () => {
    const filteredExercises = exercises.filter(exercise => {
      const matchesSearch = filterObjectsByString(searchValue, [exercise]).length > 0;
      const matchesStatus = showInactive || exercise.statusId === 1;
      return matchesSearch && matchesStatus;
    });

    if (selectedExercises.length === filteredExercises.length) {
      setSelectedExercises([]);
    } else {
      setSelectedExercises(filteredExercises);
    }
  };

  const handleConfirm = async () => {
    // Prepare data for backend
    const attachmentData = {
      exerciseIds: selectedExercises.map(e => e.id),
      userIds: [user.Users.Id],
      companyId: admin.companyId
    };

    try {
      // Call the bulk assign exercises endpoint
      await bulkAssignExercises(attachmentData).unwrap();
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
      
      // Close the modal after successful assignment
      onClose();
      
    } catch (error: any) {
      console.error("Error assigning exercises:", error);
      
      // Show error indicator
      setShowError(true);
      setErrorMessage(error?.data?.message || error?.message || "Failed to assign exercises. Please try again.");
    }
  };

  if (!isOpen) return null;

  // Filter exercises based on search and status
  const filteredExercises = exercises.filter(exercise => {
    const matchesSearch = filterObjectsByString(searchValue, [exercise]).length > 0;
    const matchesStatus = showInactive || exercise.statusId === 1;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {t("SELECT_EXERCISES")} - {user.Users.FirstName} {user.Users.LastName}
          </h2>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        {showError ? (
          <div className={styles.stepContent}>
            <div className={styles.errorContainer}>
              <div className={styles.errorIcon}>❌</div>
              <h3 className={styles.errorTitle}>{t("ERROR")}</h3>
              <p className={styles.errorMessage}>
                {errorMessage}
              </p>
              <button 
                className={`addBtn ${styles.retryButton}`}
                onClick={() => {
                  setShowError(false);
                  setErrorMessage("");
                }}
              >
                {t("ONBOARDING_BACK")}
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.stepContent}>
            <div className={styles.searchContainer}>
              <input
                type="text"
                placeholder={t('SEARCH_FOR')}
                className="searchInput"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={() => setShowInactive(!showInactive)}
                />
                {t("SHOW_INACTIVE_EXERCISES")}
              </label>
              <button 
                className={`addBtn ${styles.selectAllBtn}`}
                onClick={handleSelectAll}
              >
                {selectedExercises.length === filteredExercises.length && filteredExercises.length > 0
                  ? t("CLEAR") 
                  : t("SELECT_ALL")
                }
              </button>
            </div>

            <div className={styles.exercisesList}>
              <ul className="list">
                <li className={`listRow listRow_header ${styles.exerciseListRow}`}>
                  <p className={styles.modalListCellHeader}></p>
                  <p className={styles.modalListCellHeader}>{t("TITLE")}</p>
                  <p className={styles.modalListCellHeader}>{t("DESCRIPTION")}</p>
                  <p className={styles.modalListCellHeader}>{t("CATEGORY")}</p>
                  <p className={styles.modalListCellHeader}>{t("COLUMN_STATUS")}</p>
                </li>
                
                {filteredExercises.map((exercise, index) => (
                  <li 
                    key={exercise.id} 
                    className={`listRow ${index % 2 === 0 && "listRow_colored"} ${styles.exerciseListRow}`}
                    onClick={() => handleExerciseToggle(exercise)}
                    style={{ cursor: 'pointer' }}
                  >
                    <p className={styles.modalListCell}>
                      <input
                        type="checkbox"
                        checked={selectedExercises.some(e => e.id === exercise.id)}
                        readOnly
                      />
                    </p>
                    <p className={styles.modalListCell}>{exercise.title}</p>
                    <p className={styles.modalListCell}>{exercise.about}</p>
                    <p className={styles.modalListCell}>
                      {getCategory(exercisesCategories, exercise.exercisesCategoriesId)}
                    </p>
                    <p className={styles.modalListCell}>
                      {exercise.statusId === 0 ? t("STATUS_INACTIVE") : t("STATUS_ACTIVE")}
                    </p>
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.stepActions}>
              <button className="addBtn" onClick={onClose}>
                {t("CANCEL")}
              </button>
              <button
                className={`addBtn ${(selectedExercises.length === 0 || isAssigning) ? styles.disabled : ''}`}
                onClick={handleConfirm}
                disabled={selectedExercises.length === 0 || isAssigning}
              >
                {isAssigning ? t("LOADING") : `${t("CONFIRM")} (${selectedExercises.length})`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExerciseSelectionModal;